import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CompanyCtx = createContext(null);
const STORAGE_KEY = "dosoft.active_company_id";
export const ALL_COMPANIES = "__all__";

export function CompanyProvider({ children }) {
  const { user, checking, isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/companies");
      const list = r.data || [];
      setCompanies(list);

      const stored = localStorage.getItem(STORAGE_KEY);

      // Admin: "Todas las empresas" is a valid selection. Default to ALL if nothing stored.
      if (isAdmin) {
        if (stored === ALL_COMPANIES) {
          setActiveIdState(ALL_COMPANIES);
        } else if (stored && list.find((c) => c.id === stored)) {
          setActiveIdState(stored);
        } else {
          setActiveIdState(ALL_COMPANIES);
          localStorage.setItem(STORAGE_KEY, ALL_COMPANIES);
        }
        return;
      }

      // Seller: auto-pick first assigned company.
      const existing = list.find((c) => c.id === stored);
      if (!existing && list.length > 0) {
        const firstActive = list.find((c) => c.is_active) || list[0];
        setActiveIdState(firstActive.id);
        localStorage.setItem(STORAGE_KEY, firstActive.id);
      } else if (list.length === 0) {
        setActiveIdState(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // not authenticated yet — silent
    } finally {
      setLoaded(true);
    }
  }, [isAdmin]);

  // Refresh when auth status resolves (user logs in/out)
  useEffect(() => {
    if (checking) return;
    if (user) {
      refresh();
    } else {
      setCompanies([]);
      setActiveIdState(null);
      localStorage.removeItem(STORAGE_KEY);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, checking]);

  const setActiveId = (id) => {
    setActiveIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const isAll = activeId === ALL_COMPANIES;
  const active = isAll ? null : companies.find((c) => c.id === activeId) || null;

  return (
    <CompanyCtx.Provider
      value={{ companies, active, activeId, isAll, setActiveId, refresh, loaded }}
    >
      {children}
    </CompanyCtx.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyCtx);
  if (!ctx) throw new Error("useCompany must be used inside <CompanyProvider />");
  return ctx;
}
